import React, { useState, useEffect, useRef } from "react";
import GoogleMaps from "../../common/GoogleMaps";
import Input from "../../common/FormInputs";
import PhoneFormInput from "../../common/PhoneInput";
import InputTextArea from "../../common/InputTextArea";
import Loader from "../../common/Loader";
import { uploadFile } from "../../../utils/s3";
import { ReactComponent as CameraIcon } from "../../../Assets/Icons/cameraIcon.svg";

import { useSelector, useDispatch } from "react-redux";
import Selector from "../../common/Selector";
import { getReasons, createReason } from "../../../services/onboardingService";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { getFundraiserCategories, getSubCategoriesByCategoryId } from '../../../services/fundraiserService';

const EditProfile = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  setPlaceId,
  setLogoLoading,
  logoLoading,
  resetKey,
  onCustomReasonCreated,
  setFieldTouched,
}) => {
  const [showMap, setShowMap] = useState(false);
  const [logoFileImage, setLogoFileImage] = useState();
  const [logoFileData, setLogoFileData] = useState();
  const [logoUrl, setLogoUrl] = useState();
  const [logoPreviewStyle, setLogoPreviewStyle] = useState({});
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  const dispatch = useDispatch();
  const [allReasons, setAllReasons] = useState([]);
  const [newReason, setNewReason] = useState("");
  const [customReasonId, setCustomReasonId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  useEffect(() => {
    setLogoFileImage(undefined);
    setLogoFileData(undefined);
    setLogoUrl(undefined);
    setLogoPreviewStyle({});
  }, [resetKey]);

  useEffect(() => {
    if (role === "fundraiser") {
      dispatch(getReasons())
        .unwrap()
        .then((res) => {
          if (res?.success && res?.data) setAllReasons(res.data);
        });
    }
  }, [dispatch, role]);

  useEffect(() => {
    setLogoPreviewStyle({});
  }, [logoFileImage, values?.logo]);

  // Fetch categories on mount
  useEffect(() => {
    if (role === 'fundraiser') {
      dispatch(getFundraiserCategories())
        .unwrap()
        .then((res) => {
          if (res?.success && res?.data) setCategories(res.data);
        });
    }
  }, [dispatch, role]);

  // Normalize existing fundraiser causes to IDs once reasons are loaded
  useEffect(() => {
    if (role === 'fundraiser' && allReasons.length > 0) {
      const selected = values.fundraiserCause || [];
      const hasNonIds = selected.some((v) => typeof v === 'string' && !/^[0-9a-fA-F]{24}$/.test(v) && v !== 'Other');
      if (hasNonIds) {
        const normalized = selected.map((v) => {
          if (v === 'Other') return v;
          if (/^[0-9a-fA-F]{24}$/.test(v)) return v;
          const found = allReasons.find((r) => r.reason === v);
          return found?._id || v;
        });
        setFieldValue('fundraiserCause', normalized);
      }
    }
  }, [role, allReasons, values.fundraiserCause, setFieldValue]);

  // Normalize sub-category IDs to titles for display once subcategories are loaded (only once, prevent loops)
  const hasNormalizedSubsRef = useRef(false);

  // Fetch subcategories when fundraiserCategory changes
  useEffect(() => {
    if (role === 'fundraiser' && values.fundraiserCategory) {
      // Reset normalization flag when category changes
      hasNormalizedSubsRef.current = false;
      const selectedCat = categories.find(cat => cat.title === values.fundraiserCategory || cat._id === values.fundraiserCategory);
      if (selectedCat?._id) {
        // Debug
        try { if (process.env.NODE_ENV !== 'production') console.debug('[EditProfile] Fetching subcategories for categoryId:', selectedCat._id, 'title:', selectedCat.title); } catch (_) { }
        dispatch(getSubCategoriesByCategoryId({ id: selectedCat._id }))
          .unwrap()
          .then((res) => {
            if (res?.success && res?.data) setSubCategories(res.data);
            // Debug
            try { if (process.env.NODE_ENV !== 'production') console.debug('[EditProfile] subCategories fetched:', (res?.data || []).map(s => ({ id: s._id, title: s.title }))); } catch (_) { }
          });
      }
      // Hide subcategory and clear value if Community Organisations is selected
      if (values.fundraiserCategory === 'Community Organisations') {
        setFieldValue('fundraiserSubCategory', []);
      }
    }
  }, [dispatch, role, values.fundraiserCategory, categories, setFieldValue]);

  // Normalize sub-category IDs to titles for display once subcategories are loaded (only once, prevent loops)
  useEffect(() => {
    if (role === 'fundraiser' && subCategories.length > 0 && !hasNormalizedSubsRef.current) {
      const selected = values.fundraiserSubCategory || [];
      // If any value looks like an ID, convert to title for display
      const hasIds = selected.some((v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v));
      if (hasIds) {
        const normalized = selected.map((v) => {
          if (typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v)) {
            const found = subCategories.find((s) => s._id === v);
            return found ? found.title : v;
          }
          return v;
        }).filter((t) => typeof t === 'string' && t.length > 0);
        if (normalized.length > 0) {
          hasNormalizedSubsRef.current = true;
          setFieldValue('fundraiserSubCategory', normalized, false);
        } else {
          hasNormalizedSubsRef.current = true;
        }
      } else {
        hasNormalizedSubsRef.current = true;
      }
    }
  }, [role, subCategories, setFieldValue]);

  // Prepare options
  const fundraiserCategoryOptions = categories.map(cat => ({ value: cat.title, label: cat.title }));

  // Use sub-category titles as values so displayed tags show titles and can be removed reliably
  const subCategoryOptions = subCategories.map(sub => ({ value: sub.title, label: sub.title }));
  // Debug current value/options
  try {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[EditProfile] SubCategory selector state', {
        valueIds: values?.fundraiserSubCategory,
        optionsCount: subCategoryOptions.length,
        firstOptions: subCategoryOptions.slice(0, 6)
      });
    }
  } catch (_) { }

  const validateImage = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (
      extFile === "jpg" ||
      extFile === "jpeg" ||
      extFile === "png" ||
      extFile === "svg"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleUploadLogo = async (event) => {
    const originalFile = event.target.files && event.target.files[0];
    if (!originalFile) return;
    const fileName = originalFile.name;

    // Compress only by quality to keep dimensions, target <= 1MB
    const ONE_MB = 1024 * 1024;
    const compressToUnder1MB = (file) =>
      new Promise((resolve) => {
        if (file.size <= ONE_MB) {
          resolve({ file, name: file.name });
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = async () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0, img.width, img.height);
            const targetMime = "image/jpeg";
            let quality = 0.92;
            const toBlobQ = (q) => new Promise((res) => canvas.toBlob((b) => res(b), targetMime, q));
            let blob = await toBlobQ(quality);
            while (blob && blob.size > ONE_MB && quality > 0.6) {
              quality -= 0.05;
              blob = await toBlobQ(quality);
            }
            const outName = file.name.replace(/\.(png|jpeg|jpg)$/i, ".jpg");
            resolve({ file: new File([blob], outName, { type: targetMime }), name: outName });
          };
          img.onerror = () => resolve({ file, name: file.name });
          img.src = reader.result;
        };
        reader.onerror = () => resolve({ file, name: file.name });
        reader.readAsDataURL(file);
      });

    const { file: fileToUpload, name: finalName } = await compressToUnder1MB(originalFile);

    setLogoLoading && setLogoLoading(true);
    const s3Url = await uploadFile(fileToUpload, finalName);
    if (s3Url) {
      setLogoUrl(s3Url);
      setLogoLoading && setLogoLoading(false);
      // Persist the uploaded URL into the form state so save sends a valid logo URL
      try { setFieldValue("logo", s3Url, false); } catch (_) { }
    }

    const previewUrl = URL.createObjectURL(fileToUpload);
    setLogoFileImage(previewUrl);
    try {
      localStorage.setItem("profilePic", previewUrl);
      localStorage.setItem("LATEST_UPLOADED_LOGO", previewUrl);
    } catch (_) { }
    setLogoFileData(fileToUpload);
    setLogoPreviewStyle({});
  };

  const handleClearLogo = () => {
    setFieldValue("logo", "");
    setLogoFileImage(undefined);
    setLogoFileData(undefined);
    setLogoPreviewStyle({});
  };

  // const handleLocationClick = () => {
  //   setShowMap(true);
  // };

  const handleLocationSelect = (selectedLocation) => {
    const val = selectedLocation?.placeName || "";
    setFieldValue("address", val, true);
    setFieldTouched("address", true, true);
    setPlaceId(selectedLocation?.placeId);
    setFieldValue("locationLat", selectedLocation?.lat);
    setFieldValue("locationLng", selectedLocation?.lng);
    setFieldValue("locationCity", selectedLocation?.city || "");
    setFieldValue("locationState", selectedLocation?.state || "");
    setFieldValue("locationCountry", selectedLocation?.country || "");
    setFieldValue("locationZip", selectedLocation?.zipCode || "");
  };
  console.log("Logo", `${process.env.REACT_APP_S3_URL + values?.logo}`);

  return (
    <div>
      <>
        <div className="flex flex-col lg:flex-row flex-wrap w-full mt-8">
          <Input
            type="text"
            label=""
            placeholder={`${role === "merchant"
              ? "Business Name"
              : role === "fundraiser"
                ? "Fundraiser Name"
                : "Name"
              }`}
            width="100%"
            radius="5px"
            InputDivClass="w-full lg:w-[47.5%]"
            name="businessName"
            data-field="businessName"
            value={values?.businessName}
            handleChange={(e) => {
              const val = e.target.value;
              // Allow only letters and spaces
              if (/^[A-Za-z\s]*$/.test(val)) {
                handleChange(e);
              }
            }}
            handleBlur={(e) => {
              handleBlur(e);
              setFieldValue("businessName", values.businessName, true);
            }}
            error={errors.businessName}
            touched={touched.businessName}
          />
          <div className="lg:w-[5%]"></div>
          <Input
            type="text"
            label=""
            placeholder="Email"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-8 lg:mt-0 lg:w-[47.5%]"
            name="email"
            data-field="email"
            value={values?.email}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
            readonly={true}
            inputClass="cursor-not-allowed !bg-[#F9F9F9]"
          />
        </div>
        {role !== "admin" && (
          <>
            <div className="flex flex-col lg:flex-row flex-wrap w-full mt-8">
              <Input
                type="text"
                label=""
                placeholder="Website URL"
                width="100%"
                radius="5px"
                InputDivClass="w-full lg:w-[47.5%]"
                name="websiteUrl"
                data-field="websiteUrl"
                value={values?.websiteUrl}
                handleChange={handleChange}
                handleBlur={(e) => {
                  handleBlur(e);
                  setFieldValue("websiteUrl", values.websiteUrl, true);
                }}
                error={errors.websiteUrl}
                touched={touched.websiteUrl}
              />
              <div className="lg:w-[5%]"></div>
              <PhoneFormInput
                label={""}
                divClassName="w-full mt-8 lg:mt-0 lg:w-[47.5%]"
                name="contact"
                data-field="contact"
                value={values?.contact}
                handleBlur={(e) => {
                  handleBlur(e);
                  setFieldValue("contact", values.contact, true);
                }}
                error={errors.contact}
                touched={touched.contact}
                setFieldValue={setFieldValue}
                inputStyle={{
                  width: "100%",
                  border: "none",
                }}
                buttonStyle={{ border: "none" }}
                buttonClass="!rounded-l-[5px] !bg-[#D9D9D9] !w-[22%] md:!w-[10%] sm:!w-[15%] lg:!w-[13%] xl:!w-[10%] 2xl:!w-[10%]"
                containerClass="!rounded-[5px]"
                inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[16px] sm:!text-[14px] !font-[400] !outline-none !font-poppins"
                labelClass="mb-8"
                errorClass={
                  "text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1"
                }
              />
            </div>
            {/* Show Upload Logo for all roles - Moved below Website/Phone row */}
            <div className="flex mt-8 items-center gap-8 justify-start w-full" data-field="logo">
              <div
                className="relative h-[80px] w-[80px] rounded-[10px] border-2 border-solid border-[#E4E3E4] overflow-hidden bg-white flex justify-center items-center logo cursor-pointer"
                onClick={() => {
                  try {
                    const el = document.getElementById("fileInput");
                    if (el) el.click();
                  } catch (_) { }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    try {
                      const el = document.getElementById("fileInput");
                      if (el) el.click();
                    } catch (_) { }
                  }
                }}
              >
                {logoLoading === true && <Loader />}
                {logoFileImage !== undefined || values?.logo !== "" ? (
                  <img
                    src={(() => {
                      if (logoFileImage) return logoFileImage;
                      const v = values?.logo || "";
                      if (typeof v === "string" && /^https?:\/\//i.test(v)) return v;
                      return `${process.env.REACT_APP_S3_URL}${v}`;
                    })()}
                    alt="logo"
                    className="select-none"
                    style={{
                      imageRendering: "auto",
                      borderRadius: "10px",
                      display: "block",
                      ...logoPreviewStyle,
                    }}
                    onLoad={(event) => {
                      try {
                        const { naturalWidth, naturalHeight } = event.target || {};
                        if (!naturalWidth || !naturalHeight) {
                          setLogoPreviewStyle({});
                          return;
                        }
                        const widthRatio = naturalWidth / naturalHeight;
                        const heightRatio = naturalHeight / naturalWidth;
                        const widthMultiplier = 1.15;
                        const heightMultiplier = 1.08;
                        setLogoPreviewStyle({
                          width:
                            widthRatio >= 1
                              ? `${(widthRatio || 1) * 100 * widthMultiplier}%`
                              : `${100 * widthMultiplier}%`,
                          height:
                            heightRatio >= 1
                              ? `${(heightRatio || 1) * 100 * heightMultiplier}%`
                              : `${100 * heightMultiplier}%`,
                        });
                      } catch (error) {
                        setLogoPreviewStyle({});
                      }
                    }}
                  />
                ) : (
                  <CameraIcon />
                )}
              </div>
              <input
                type="file"
                id="fileInput"
                hidden
                accept=".jpg,.jpeg,.png,.svg"
                onChange={handleUploadLogo}
                onClick={(event) => {
                  event.target.value = null;
                }}
              />
              {logoFileImage !== undefined || (values?.logo && values?.logo !== "") ? (
                <p
                  className="font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353] cursor-pointer transition-colors hover:text-red-500"
                  onClick={handleClearLogo}
                >
                  Clear
                </p>
              ) : (
                <div className="flex flex-col justify-start">
                  <p
                    className="font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353]"
                  >
                    {role === "fundraiser" ? "Upload Fundraiser Logo" : "Upload Merchant Logo"}
                  </p>
                  <p className="font-poppins font-[400] text-[10px] md:text-[12px] text-[#00adef]">
                    File type: must be an svg, jpeg, jpg, or png
                  </p>
                  {/* Removed size note per request */}
                </div>
              )}
            </div>
            {errors.logo && touched.logo ? (
              <div className="ms-1 mt-1 w-full flex justify-start items-start">
                <span className="text-red-600 text-[10px] sm:text-[11px] md:text-[12px] font-[600] text-start">
                  {errors.logo}
                </span>
              </div>
            ) : null}

            {/* Map and Address Section */}
            {/* Map and Address Section */}
            <div
              className={`w-full grid duration-500 ease-in-out ${!values.isOnlineMerchant ? "grid-rows-[1fr] opacity-100 mt-8" : "grid-rows-[0fr] opacity-0 mt-0"}`}
              data-field="address"
              style={{ transitionProperty: "grid-template-rows, opacity, margin-top" }}
            >
              <div className="overflow-hidden">
                <style>{`
                  .merchant-profile-map .gm-fullscreen-control {
                    top: 53px !important;
                  }
                  /* Force show desktop search bar on mobile */
                  .merchant-profile-map .hidden.sm\\:block {
                    display: block !important;
                    background: transparent !important;
                    overflow: visible !important;
                  }
                  /* Hide default mobile search button */
                  .merchant-profile-map .sm\\:hidden {
                    display: none !important;
                  }
                  /* Hide Map/Satellite control on mobile to avoid overlap */
                  @media (max-width: 768px) {
                    .merchant-profile-map .gm-style-mtc {
                      display: none !important;
                    }
                  }
                `}</style>
                <div className="merchant-profile-map h-[300px] md:h-auto">
                  <GoogleMaps
                    onLocationSelect={handleLocationSelect}
                    searchBarParentClassName="!w-[90%] md:!w-[60%] lg:!w-[50%] absolute top-[10px] left-0 right-0 mx-auto md:right-[10px] md:left-auto md:mx-0 z-10"
                    defaultAddress={values.address}
                    placeholder={role === "fundraiser" ? "Add fundraiser address" : "Add business Address"}
                    searchBarDivClassName="w-full flex items-center h-[50px] relative bg-transparent"
                    searchBarInputClassName="navSearchBar h-full w-[100%] ps-[3%] pe-[12%] text-[16px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[5px] border border-[#D9D9D9] placeholder:!text-[#767676]"
                    searchIconClassName="absolute right-[3%] w-[20px] h-[20px]"
                    defaultCenter={
                      values.locationLat && values.locationLng
                        ? { lat: parseFloat(values.locationLat), lng: parseFloat(values.locationLng) }
                        : undefined
                    }
                    onBlur={() => {
                      setFieldTouched("address", true, true);
                      setFieldValue("address", values.address, true);
                    }}
                  />
                </div>
                {errors.address && (touched.address || (values.address === "" && Object.keys(touched).length > 0)) && (
                  <div className="ms-1 mt-2 w-full flex justify-start items-start">
                    <span className="text-red-600 text-[12px] sm:text-[13px] md:text-[14px] font-[600] text-start">
                      {errors.address}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Checkbox for online merchant - Hide for fundraiser */}
            {role !== "fundraiser" && (
              <div className={`w-full ${!values.isOnlineMerchant ? "mt-4" : "mt-8"} p-4 bg-white border border-[#D9D9D9] rounded-[5px]`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isOnlineMerchant"
                    name="isOnlineMerchant"
                    checked={values.isOnlineMerchant}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("isOnlineMerchant", checked);
                      if (checked) {
                        setFieldValue("address", "Australia");
                        setFieldValue("locationCity", "");
                        setFieldValue("locationState", "");
                        setFieldValue("locationCountry", "Australia");
                        setFieldValue("locationZip", "");
                        setFieldValue("locationLat", -33.8688);
                        setFieldValue("locationLng", 151.2093);
                      } else {
                        // Clear values if unticking so user searches again
                        setFieldValue("address", "");
                        setFieldValue("locationLat", null);
                        setFieldValue("locationLng", null);
                      }
                    }}
                    className="peer hidden"
                  />
                  <label
                    htmlFor="isOnlineMerchant"
                    className={`w-5 h-5 border rounded-[3px] flex items-center justify-center cursor-pointer transition ${values.isOnlineMerchant
                      ? 'bg-[#00ADEF] border-[#00ADEF]'
                      : 'border-[#00ADEF] bg-white'
                      }`}
                  >
                    {values.isOnlineMerchant && (
                      <svg
                        className="w-4 h-4 transition"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </label>
                  <label
                    htmlFor="isOnlineMerchant"
                    className="font-poppins text-[12px] sm:text-[13px] md:text-[14px] text-[#303030] cursor-pointer select-none"
                  >
                    I deal in online offers and don't have a physical address.
                  </label>
                </div>

                <div
                  className={`grid duration-300 ease-in-out ${values.isOnlineMerchant ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0 mt-0"}`}
                  style={{ transitionProperty: "grid-template-rows, opacity, margin-top" }}
                >
                  <div className="overflow-hidden">
                    <div className="p-3 bg-[#F9F9F9] border border-[#E5E5E5] rounded-[5px] cursor-not-allowed">
                      <p className="font-poppins text-[13px] sm:text-[14px] md:text-[15px] text-[#767676] font-[400]">
                        Region: Australia
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <InputTextArea
              label={role === "fundraiser" ? "About Business" : "About Business"}
              placeholder={
                role === "fundraiser"
                  ? "Tell us about your business"
                  : "Tell us why customers should visit your establishment"
              }
              divClassName="relative mt-4 w-full"
              labelClassName="font-poppins text-[16px] font-[400] text-[#303030] mb-2"
              textareaClassName="w-full h-[45px] md:h-[150px] px-3 md:px-[1%] py-2 md:pt-2 rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins whitespace-nowrap md:whitespace-normal overflow-x-auto md:overflow-visible"
              name="aboutBusiness"
              data-field="aboutBusiness"
              value={values.aboutBusiness}
              handleBlur={(e) => {
                handleBlur(e);
                setFieldValue("aboutBusiness", values.aboutBusiness, true);
              }}
              error={errors.aboutBusiness}
              touched={touched.aboutBusiness}
              setFieldValue={setFieldValue}
            />
            {role === "fundraiser" && (
              <>
                <Selector
                  value={(() => {
                    // Convert IDs to reason texts for display
                    const selectedValues = values.fundraiserCause || [];
                    return selectedValues.map(id => {
                      if (id === "Other") return "Other";
                      const reason = allReasons.find(r => r._id === id);
                      return reason ? reason.reason : id;
                    });
                  })()}
                  label={"Choose your fundraiser cause"}
                  labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                  divClassName="mt-4 w-full multipleField"
                  selectClassName="w-full min-h-[45px] sm:min-h-[45px] md:min-h-[50px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[14px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer !h-auto"
                  flexClassName="gap-1"
                  data-field="fundraiserCause"
                  options={[
                    ...allReasons
                      .filter((r) => {
                        const selectedValues = values.fundraiserCause || [];
                        // Check if this reason is already selected (by ID)
                        const isSelected = selectedValues.includes(r._id);
                        return !isSelected;
                      })
                      .map((r) => ({ label: r.reason, value: r.reason })),
                    { label: "Other", value: "Other" }
                  ]}
                  suffixIcon={<span className="cursor-pointer"><DownArrow /></span>}
                  placeholder={"Select or add cause"}
                  onChange={(value) => {
                    // Convert reason texts back to IDs for storage
                    const convertedValue = value.map(item => {
                      if (item === "Other") return "Other";
                      const reason = allReasons.find(r => r.reason === item);
                      return reason ? reason._id : item;
                    });
                    setFieldValue("fundraiserCause", convertedValue);
                  }}
                  handleBlur={(e) => {
                    handleBlur(e);
                    setFieldValue('fundraiserCause', values.fundraiserCause, true);
                  }}
                  error={errors.fundraiserCause}
                  touched={touched.fundraiserCause}
                  multiple={true}
                  mode="tags"
                />
                {(values.fundraiserCause || []).includes("Other") && (
                  <div className="relative w-full mt-4">
                    <InputTextArea
                      label={""}
                      placeholder={"Write other reason..."}
                      divClassName="w-full"
                      labelClassName="invitationLabels"
                      textareaClassName="w-full h-[100px] px-4 pt-3 rounded-[10px] bg-[#F2F2F2] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins pr-24 pb-12"
                      name="newReason"
                      value={newReason}
                      handleBlur={handleBlur}
                      error={errors.newReason}
                      touched={touched.newReason}
                      setFieldValue={(_, val) => setNewReason(val)}
                      showCounter={false}
                    />
                    <div className="absolute bottom-4 right-4">
                      <button
                        type="button"
                        className="font-poppins text-[13px] sm:text-[12px] md:text-[13px] lg:text-[14px] text-center font-[500] h-[32px] sm:h-[32px] md:h-[36px] lg:h-[38px] rounded-[7px] px-3 bg-[#00ADEF] text-white"
                        disabled={
                          !newReason ||
                          newReason.trim() === "" ||
                          (values.fundraiserCause || []).includes(newReason.trim())
                        }
                        onClick={async () => {
                          if (
                            newReason &&
                            newReason.trim() !== "" &&
                            !(values.fundraiserCause || []).includes(newReason.trim())
                          ) {
                            try {
                              // Call the API to create the reason (for backend record)
                              const createResponse = await dispatch(createReason({ reason: newReason.trim() }))?.unwrap();

                              if (createResponse?.success === true) {
                                // Store the created reason ID
                                setCustomReasonId(createResponse?.data?._id);

                                // Notify parent component about the custom reason
                                if (onCustomReasonCreated) {
                                  onCustomReasonCreated(createResponse?.data?._id);
                                }

                                // Remove "Other" and add the new reason text temporarily for display
                                const updated = [
                                  ...(values.fundraiserCause || []).filter((r) => r !== "Other" && r.trim() !== ""),
                                  newReason.trim()
                                ];
                                setFieldValue("fundraiserCause", updated);
                                setNewReason("");
                              }
                            } catch (error) {
                              console.error("Failed to create custom reason", error);
                            }
                          }
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            {role === 'fundraiser' && (
              <>
                <Selector
                  value={values.fundraiserCategory || ""}
                  label={"Choose a fundraiser category"}
                  labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                  divClassName="mt-4 w-full multipleField"
                  selectClassName="w-full min-h-[45px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[14px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer !h-auto"
                  data-field="fundraiserCategory"
                  options={fundraiserCategoryOptions}
                  suffixIcon={<span className="cursor-pointer"><DownArrow /></span>}
                  placeholder={"Select category"}
                  onChange={(value) => {
                    setFieldValue('fundraiserCategory', value);
                  }}
                  handleBlur={(e) => {
                    handleBlur(e);
                    setFieldValue('fundraiserCategory', values.fundraiserCategory, true);
                  }}
                  error={errors.fundraiserCategory}
                  touched={touched.fundraiserCategory}
                  multiple={false}
                />
                <style>{`
                  /* Fix for mulit-select tags overflow on mobile - Horizontal Scrolling Chips */
                  .multipleField .ant-select-selection-overflow {
                    display: flex;
                    flex-wrap: nowrap !important;
                    overflow-x: auto;
                    overflow-y: hidden;
                    max-width: 100%;
                    scrollbar-width: none; /* Firefox */
                    -ms-overflow-style: none; /* IE/Edge */
                    padding-right: 20px; /* Spacer for last item */
                  }
                  .multipleField .ant-select-selection-overflow::-webkit-scrollbar {
                    display: none; /* Chrome/Safari/Opera */
                  }
                  .multipleField .ant-select-selection-overflow-item {
                    flex: 0 0 auto !important;
                  }
                  .multipleField .ant-select-selector {
                     display: flex !important;
                     align-items: center !important;
                     height: auto !important;
                     min-height: 45px;
                     padding-top: 4px;
                     padding-bottom: 4px;
                     padding-left: 8px;
                     padding-right: 8px;
                     overflow: visible;
                  }
                  @media (max-width: 768px) {
                    .multipleField .ant-select-selector,
                    .multipleField .ant-select-selection-item,
                    .multipleField .ant-select-selection-placeholder {
                      font-size: 14px !important;
                    }
                  }
                  /* Vertical centering for single selects (Category) */
                  .multipleField .ant-select-single .ant-select-selection-item,
                  .multipleField .ant-select-single .ant-select-selection-placeholder {
                     display: flex !important;
                     align-items: center !important;
                     line-height: normal !important;
                  }
                `}</style>
                {/* Only show Sub Category if not Community Organisations */}
                {values.fundraiserCategory !== 'Community Organisations' && (
                  <>
                    <Selector
                      value={values.fundraiserSubCategory || []}
                      label={"Select Fundraiser Sub Category"}
                      labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
                      divClassName="mt-4 w-full multipleField"
                      selectClassName="w-full min-h-[45px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[16px] sm:text-[14px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer !h-auto"
                      flexClassName="gap-1"
                      data-field="fundraiserSubCategory"
                      options={subCategoryOptions}
                      suffixIcon={<span className="cursor-pointer"><DownArrow /></span>}
                      placeholder={"Select Fundraiser Sub Category"}
                      onChange={(value) => {
                        // Debug
                        try { if (process.env.NODE_ENV !== 'production') console.debug('[EditProfile] SubCategory onChange value:', value); } catch (_) { }
                        setFieldValue('fundraiserSubCategory', value);
                      }}
                      handleBlur={(e) => {
                        handleBlur(e);
                        setFieldValue('fundraiserSubCategory', values.fundraiserSubCategory, true);
                      }}
                      error={errors.fundraiserSubCategory}
                      touched={touched.fundraiserSubCategory}
                      multiple={true}
                    />

                    {/* --- NEW FIELDS BELOW --- */}
                    <div className="w-full mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                      <Input
                        type="text"
                        label="Organisation Name"
                        placeholder="Enter Organisation Name"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="organisationName"
                        data-field="organisationName"
                        value={values.organisationName}
                        handleChange={handleChange}
                        handleBlur={(e) => {
                          handleBlur(e);
                          setFieldValue("organisationName", values.organisationName, true);
                        }}
                        error={errors.organisationName}
                        touched={touched.organisationName}
                      />
                      <Input
                        type="text"
                        label="ABN"
                        placeholder="Enter ABN"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="abn"
                        data-field="abn"
                        value={values.abn}
                        handleChange={handleChange}
                        handleBlur={(e) => {
                          handleBlur(e);
                          setFieldValue("abn", values.abn, true);
                        }}
                        error={errors.abn}
                        touched={touched.abn}
                      />
                      <Input
                        type="text"
                        label="Charity Registration Number"
                        placeholder="Enter Charity Registration Number"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="charityRegistrationNumber"
                        data-field="charityRegistrationNumber"
                        value={values.charityRegistrationNumber}
                        handleChange={handleChange}
                        handleBlur={(e) => {
                          handleBlur(e);
                          setFieldValue("charityRegistrationNumber", values.charityRegistrationNumber, true);
                        }}
                        error={errors.charityRegistrationNumber}
                        touched={touched.charityRegistrationNumber}
                      />
                      <Input
                        type="email"
                        label="Accounts Email"
                        placeholder="Enter Accounts Email"
                        width="100%"
                        radius="5px"
                        InputDivClass="w-full"
                        name="accountsEmail"
                        data-field="accountsEmail"
                        value={values.accountsEmail}
                        handleChange={handleChange}
                        handleBlur={(e) => {
                          handleBlur(e);
                          setFieldValue("accountsEmail", values.accountsEmail, true);
                        }}
                        error={errors.accountsEmail}
                        touched={touched.accountsEmail}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    </div >
  );
};

export default EditProfile;